//Modules
import { useState } from "react";
import { MailIcon, PhoneIcon } from "@heroicons/react/outline";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

//Components
import Loader from "../components/core/loader/loader";
import Layout from "../components/shared/layout/general";

//Hooks
import { useAlert } from "../lib/hooks/alert";

const schema = yup
  .object({
    fullName: yup.string().required("Please provide your name"),
    email: yup.string().email().required("Please enter your email"),
    phone: yup
      .string()
      .required("Please provide your preferred contact number"),
    message: yup.string().required("Please enter a message"),
    recaptcha: yup.string().required("Please complete recaptcha"),
  })
  .required();

const Content = () => {
  const [submitting, setSubmitting] = useState(false);
  const { setAlert } = useAlert();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  async function onSubmit(data) {
    setSubmitting(true);
    try {
      // api call here.
      let email_api = await axios({
        method: "post",
        url: "/api/email/contact",
        headers: {
          "Content-Type": "application/json",
          Authorization: "2jHCdX4Uc3p0ZXB",
        },
        data,
      });
      reset();
      // reset form inputs
      setAlert({
        title: "Successfully sent your message",
        message:
          "Thank you for your message, someone will be in touch shortly!",
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setAlert({
        title: "Failed to send your message",
        message: "Whoops, something went wrong, please try again",
        type: "error",
      });
    }

    setSubmitting(false);
  }

  async function handleReCAPTCHA(token) {
    let result = await axios({
      method: "post",
      url: "/api/recaptcha",
      headers: {
        "Content-Type": "application/json",
        Authorization: "ZxbqflYv9TgNec5",
      },
      data: token,
    }).catch((error) => console.log(error));

    // fill the recaptcha field if recaptcha successful so form can submit
    // (yup schema stops it submitting if there is no value present)
    if (result.data.success) {
      setValue("recaptcha", "success");
    }
    // If recaptcha fails, setError so form can't submit.
    else {
      setError("recaptcha", {
        type: "manual",
        message: "ReCAPTCHA failed, please try again",
      });
    }
  }

  return (
    <div className="relative bg-white">
      <div className="absolute inset-0">
        <div className="absolute inset-y-0 left-0 w-1/2 bg-gray-50" />
      </div>
      <div className="relative max-w-7xl mx-auto lg:grid lg:grid-cols-5">
        <div className="bg-gray-50 py-16 px-4 sm:px-6 lg:col-span-2 lg:px-8 lg:py-24 xl:pr-12">
          <div className="max-w-lg mx-auto">
            <h2 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
              Get in touch
            </h2>
            <p className="mt-3 text-lg leading-6 text-gray-500">
              Reach out by e-mail, use this form, or call us and we'll be in
              touch as soon as possible!
            </p>
            <dl className="mt-8 text-base text-gray-500">
              <div>
                <dt className="sr-only">Postal address</dt>
                <dd>
                  <p>Mailing Address / Office Location:</p>
                  <p>373 Margaret King Ave</p>
                  <p>Ringwood, NJ 07456</p>
                </dd>
              </div>
              <div>
                <dt className="sr-only">Google Maps</dt>
                <dd>
                  <p className="mt-4">
                    Google Maps:{" "}
                    <a
                      href="https://g.page/wavedistro"
                      target="_blank"
                      className="underline"
                      rel="noreferrer"
                    >
                      https://g.page/wavedistro
                    </a>
                  </p>
                </dd>
              </div>
              <div className="mt-6">
                <dt className="sr-only">Phone number</dt>
                <dd className="flex">
                  <PhoneIcon
                    className="flex-shrink-0 h-6 w-6 text-gray-400"
                    aria-hidden="true"
                  />
                  <span className="ml-3">+1 (973) 728-2425</span>
                </dd>
              </div>
              <div className="mt-3">
                <dt className="sr-only">Email</dt>
                <dd className="flex">
                  <MailIcon
                    className="flex-shrink-0 h-6 w-6 text-gray-400"
                    aria-hidden="true"
                  />
                  <span className="ml-3">info@wavedistro.com</span>
                </dd>
              </div>
            </dl>
            {/* <p className="mt-6 text-base text-gray-500">
              Looking to reseller or distribute Wave products?{" "}
              <a href="#" className="font-medium text-gray-700 underline">
                Submit an application
              </a>
              .
            </p> */}
          </div>
        </div>
        <div className="bg-white py-16 px-4 sm:px-6 lg:col-span-3 lg:py-24 lg:px-8 xl:pl-12">
          <div className="max-w-lg mx-auto lg:max-w-none">
            <form
              onSubmit={handleSubmit((data) => onSubmit(data))}
              className="grid grid-cols-1 gap-y-6"
            >
              <div>
                <label htmlFor="full-name" className="sr-only">
                  Full name
                </label>
                <input
                  {...register("fullName")}
                  type="text"
                  name="fullName"
                  id="fullName"
                  autoComplete="name"
                  className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                  placeholder="Full name"
                />
                {errors.fullName && (
                  <p className="mt-2 text-sm text-red-600" id="fullName-error">
                    {errors.fullName?.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <input
                  {...register("email")}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                  placeholder="Email"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors.email?.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="phone" className="sr-only">
                  Phone
                </label>
                <input
                  {...register("phone")}
                  type="text"
                  name="phone"
                  id="phone"
                  autoComplete="tel"
                  className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500 border-gray-300 rounded-md"
                  placeholder="Phone"
                />
                {errors.phone && (
                  <p className="mt-2 text-sm text-red-600" id="phone-error">
                    {errors.phone?.message}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="message" className="sr-only">
                  Message
                </label>
                <textarea
                  {...register("message")}
                  id="message"
                  name="message"
                  rows={4}
                  className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-indigo-500 focus:border-indigo-500 border border-gray-300 rounded-md"
                  placeholder="Message"
                  defaultValue={""}
                />
                {errors.message && (
                  <p className="mt-2 text-sm text-red-600" id="message-error">
                    {errors.message?.message}
                  </p>
                )}
              </div>
              <div>
                <ReCAPTCHA
                  {...register("recaptcha")}
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA}
                  onChange={handleReCAPTCHA}
                />
                {errors.recaptcha && (
                  <p className="mt-2 text-sm text-red-600" id="recaptcha-error">
                    {errors.recaptcha?.message}
                  </p>
                )}
              </div>
              <div>
                <button
                  type="submit"
                  className="inline-flex justify-center py-3 px-6 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-gray-800 hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  {submitting ? <Loader height={25} width={50} /> : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const Company = () => {
  return (
    <Layout>
      <Content />
    </Layout>
  );
};

export default Company;
